// Pure, framework-free classification of the onboarding-candidate read (design
// docs/projects/onboarding-candidates-view-design.md, D2). The GET
// /franchise-onboarding-candidates endpoint is a thin FACTUAL projection — one row per LK/SI-flagged
// Dash company with readiness facts — and this helper derives each row's onboarding STATUS, the
// action-first sort, and the summary counts. Keeping the derivation here (not in SQL) makes it
// unit-testable without a DB, mirroring eligibilityRollup.js.
//
// The facts arrive from SQL as integer 0/1 (JSON numbers), but treat them tolerantly (1 | true | "1")
// so a driver/serialization change can't silently flip a status — the same BIT-vs-boolean trap
// eligibilityRollup guards against.

const isTrue = (v) => v === 1 || v === true || v === "1";

// Status vocabulary, most-actionable first. `order` drives the action-first sort; the label is the
// human string; the component maps the key → chip colour / subdued styling.
// NB: the status KEYS are stable identifiers; only the display LABELS are worded for the UI.
//   - `needs_onboarding` => "Not onboarded": a FACT (no warehouse data), not a judgement that it should
//     be onboarded — every row here rests on the self-reported isLKSI flag, which may be stale.
//   - `data_landed_unconfigured` => "No eligibility period": data is present but no LK/SI eligibility
//     period exists (distinct from the isLKSI flag and from warehouse config); the "warehouse ready"
//     half is conveyed by the Warehouse Data = Yes column beside it.
export const STATUS_META = {
  needs_onboarding: { label: "Not onboarded", order: 0 },
  data_landed_unconfigured: { label: "No eligibility period", order: 1 },
  onboarded: { label: "Onboarded", order: 2 },
};

// A candidate's onboarding status from its readiness facts:
//   warehouse_data_found = 0                    => needs_onboarding          (THE ping: flagged, no data)
//   data landed, no eligibility row             => data_landed_unconfigured  (onboarded, needs Audit config)
//   data landed + has eligibility row           => onboarded                 (fully set up)
export function classifyCandidate(candidate = {}) {
  if (!isTrue(candidate.warehouse_data_found)) return "needs_onboarding";
  if (!isTrue(candidate.has_eligibility)) return "data_landed_unconfigured";
  return "onboarded";
}

// Attach `status` to every candidate and sort action-first (onboarded last), then by com_code so the
// order is stable and onboarded rows sink to the bottom (design D3: show all, action-first).
export function rollupCandidates(candidates = []) {
  const rows = candidates.map((c) => ({ ...c, status: classifyCandidate(c) }));
  rows.sort((a, b) => {
    const oa = STATUS_META[a.status].order;
    const ob = STATUS_META[b.status].order;
    if (oa !== ob) return oa - ob;
    return String(a.com_code).localeCompare(String(b.com_code));
  });
  return rows;
}

// Summary chips (design D3): total + one count per status.
export function summarizeCandidates(rows = []) {
  return {
    total: rows.length,
    needsOnboarding: rows.filter((r) => r.status === "needs_onboarding").length,
    dataLandedUnconfigured: rows.filter((r) => r.status === "data_landed_unconfigured").length,
    onboarded: rows.filter((r) => r.status === "onboarded").length,
  };
}
